import React, { CSSProperties } from "react";
import { GROUP_CONFIGURATION_TRIGGER_ID } from "testIds";
import HighlightedText from "common/Text/HighlightedText";
import Flex from "common/Boxes/Flex";
import { SignalBotsIcon } from "common/Icons/Icons";
import Link from "common/Typography/Link";
import BotConfigurationModal from "features/bots/BotForm/common/BotConfigurationModal";
import { BotType } from "@shared/types";
import { useDefaultSubGroupConfiguration } from "@shared/features/bots/BotForm/common/hooks";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import styled, { useTheme } from "styled-components";
import { Radio } from "lucide-react";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";
import useMedia from "utils/hooks/useMedia.ts";
import { MOBILE_SEARCH } from "common/constants.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";

interface DefaultGroupConfigurationMessageProps {
  style?: CSSProperties;
  subGroupId?: number;
  exchange?: string;
  isOnboarding?: boolean;
}

const DefaultGroupConfigurationMessage = ({
  style,
  subGroupId,
  exchange,
  isOnboarding,
}: DefaultGroupConfigurationMessageProps) => {
  const theme = useTheme();
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const {
    closeModal,
    openModal,
    isModalOpen,
    defaultConfiguration,
    getSubGroupDefaultConfigurationQuery,
    displayText,
  } = useDefaultSubGroupConfiguration({ subGroupId, exchange, isOnboarding });

  if (!defaultConfiguration) return null;

  const configurationModal = defaultConfiguration && (
    <BotConfigurationModal
      bot={{ ...defaultConfiguration, bot_type: BotType.SIGNAL }}
      open={isModalOpen}
      closeModal={closeModal}
      title={t("group_configuration.title")}
      isChannelConfiguration={true}
    />
  );

  if (theme.isNewLook) {
    // New look: a quiet well with a small line icon (no icon tile).
    return (
      <LoadingSpinnerOverlay
        isLoading={getSubGroupDefaultConfigurationQuery.isFetching}
        wrap={true}
        backgroundColor={theme.colors.cardLightBgColor}
        wrapStyle={{ height: "fit-content" }}
      >
        <NewLookNotice style={style} $inline={isOnboarding && !isMobile}>
          <NewLookIcon $inline={isOnboarding && !isMobile}>
            <Radio size={16} strokeWidth={1.75} />
          </NewLookIcon>
          <NewLookBody $inline={isOnboarding && !isMobile}>
            <span>{displayText}</span>
            <NewLookLink
              data-testid={GROUP_CONFIGURATION_TRIGGER_ID}
              onClick={openModal}
            >
              {t("buttons.show_configuration")}
            </NewLookLink>
          </NewLookBody>
        </NewLookNotice>
        {configurationModal}
      </LoadingSpinnerOverlay>
    );
  }

  return (
    <LoadingSpinnerOverlay
      isLoading={getSubGroupDefaultConfigurationQuery.isFetching}
      wrap={true}
      backgroundColor={theme.colors.highlightBgColor}
      wrapStyle={{ height: "fit-content" }}
    >
      <HighlightedText hideLine={true} style={style}>
        <Flex
          $alignItems={isOnboarding ? "center" : "flex-start"}
          $justifyContent={isOnboarding ? "flex-start" : "space-between"}
        >
          <SignalBotsIcon
            $size="25px"
            style={{
              marginRight: 12,
              marginTop: isOnboarding ? 0 : 4,
              flex: "none",
            }}
          />
          <Flex
            $flexDirection={isOnboarding && !isMobile ? "row" : "column"}
            $alignItems={isOnboarding && !isMobile ? "center" : "flex-start"}
          >
            <div
              style={{
                marginBottom: isOnboarding ? 0 : 4,
                marginRight: isOnboarding ? 8 : 0,
              }}
            >
              {displayText}
            </div>
            <Link
              data-testid={GROUP_CONFIGURATION_TRIGGER_ID}
              style={{
                fontWeight: 600,
                textDecoration: "underline",
              }}
              onClick={openModal}
            >
              {t("buttons.show_configuration")}
            </Link>
          </Flex>
        </Flex>
      </HighlightedText>
      {configurationModal}
    </LoadingSpinnerOverlay>
  );
};

export default DefaultGroupConfigurationMessage;

const NewLookNotice = styled.div<{ $inline?: boolean }>`
  display: flex;
  align-items: ${(p) => (p.$inline ? "center" : "flex-start")};
  gap: 12px;
  padding: 12px 16px;
  border-radius: ${NEW_LOOK_RADIUS.control}px;
  background: ${(p) => getNewLookTokens(p.theme).well};
  box-shadow: inset 0 0 0 1px ${(p) => getNewLookTokens(p.theme).hairline};
  ${newLookType.body};
  color: ${(p) => getNewLookTokens(p.theme).text};
`;
const NewLookIcon = styled.span<{ $inline?: boolean }>`
  flex: none;
  display: inline-flex;
  margin-top: ${(p) => (p.$inline ? 0 : 2)}px;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const NewLookBody = styled.div<{ $inline?: boolean }>`
  display: flex;
  flex-direction: ${(p) => (p.$inline ? "row" : "column")};
  flex-wrap: wrap;
  align-items: ${(p) => (p.$inline ? "center" : "flex-start")};
  gap: 4px 8px;
  min-width: 0;
`;
const NewLookLink = styled(Link)`
  ${newLookType.label};
  font-weight: 500;
`;
